import React, { useEffect, useState } from 'react';
import { Modal } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import { Project, User } from '../../models';
import { Button, Select } from '../../components';
import type { Value } from '../../components';

export type TriageModifyModalProps = {
    show: boolean;
    count: number;
    assignees: User[];
    projects: Project[];
    processing?: boolean;
    onClose: () => void;
    onApply: (payload: { assigneeId?: string; projectId?: string }) => void;
};

function TriageModifyModal(props: TriageModifyModalProps) {
    const {
        show, count, assignees, projects, processing = false, onClose, onApply,
    } = props;
    const { t } = useTranslation();
    const [assigneeId, setAssigneeId] = useState<Value>('none');
    const [projectId, setProjectId] = useState<Value>('none');

    useEffect(() => {
        if (!show)
            return;
        setAssigneeId('none');
        setProjectId('none');
    }, [show]);

    const canApply = assigneeId !== 'none' || projectId !== 'none';

    return (
        <Modal show={show} size="md" popup onClose={onClose}>
            <Modal.Header />
            <Modal.Body>
                <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {t('priorities.modify_title', { count })}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        {t('priorities.modify_hint')}
                    </p>
                    <div>
                        <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                            {t('priorities.assign_to')}
                        </span>
                        <Select
                            className="!mt-0"
                            onChange={setAssigneeId}
                            defaultValue={assigneeId}
                            placeholder={t('priorities.choose_assignee') as string}
                            options={[
                                { value: 'none', label: t('priorities.choose_assignee') || '' },
                                ...assignees.map((item: User) => ({
                                    value: item._id,
                                    label: item.fullname() || item.email,
                                })),
                            ]}
                        />
                    </div>
                    <div>
                        <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                            {t('priorities.assign_project')}
                        </span>
                        <Select
                            className="!mt-0"
                            onChange={setProjectId}
                            defaultValue={projectId}
                            placeholder={t('priorities.choose_project') as string}
                            options={[
                                { value: 'none', label: t('priorities.choose_project') || '' },
                                ...projects.map((item: Project) => ({
                                    value: item._id,
                                    label: item.name,
                                })),
                            ]}
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            small
                            color="none"
                            title={t('common.cancel') as string}
                            icon="close"
                            onClick={onClose}
                        />
                        <Button
                            small
                            color="primary"
                            title={t('priorities.modify_apply') as string}
                            icon={processing ? 'loading' : 'edit'}
                            onClick={() => {
                                if (!canApply || processing)
                                    return;
                                onApply({
                                    assigneeId: assigneeId === 'none' ? undefined : String(assigneeId),
                                    projectId: projectId === 'none' ? undefined : String(projectId),
                                });
                            }}
                            className={!canApply || processing ? 'opacity-50' : ''}
                        />
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default TriageModifyModal;
